import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  signal,
  effect,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { MinimalSelectComponent } from 'src/ui/components/minimal-select/minimal-select.component';

@Component({
  selector: 'app-table-paginator',
  standalone: true,
  imports: [CommonModule, MinimalSelectComponent],
  templateUrl: './table-paginator.component.html',
  styleUrls: ['./table-paginator.component.scss'],
})
export class TablePaginatorComponent implements OnInit, OnDestroy {
  @Input() showPagination = true;
  @Input() pageSizeOptions: number[] = [5, 10, 25, 50];
  @Input() itemType = '';
  @Input() itemFormatPaginationText = '';
  private _currentPage = signal(1);
  private _pageSize = signal(10);
  private _totalItems = signal(0);
  private _screenSize = signal<'small' | 'medium' | 'large'>('large');
  private resizeListener?: () => void;

  @Input()
  set currentPage(value: number) {
    this._currentPage.set(value);
  }
  get currentPage(): number {
    return this._currentPage();
  }

  @Input()
  set pageSize(value: number) {
    this._pageSize.set(value);
  }
  get pageSize(): number {
    return this._pageSize();
  }

  @Input()
  set totalItems(value: number) {
    this._totalItems.set(value);
  }
  get totalItems(): number {
    return this._totalItems();
  }

  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  totalPages = computed(() => {
    const total = Math.ceil(this._totalItems() / this._pageSize());
    return Math.max(1, total);
  });

   visiblePages = computed(() => {
  const total = this.totalPages();
  const current = this._currentPage();
  const screenSize = this._screenSize();
  const maxVisible = screenSize === 'small' ? 2 : screenSize === 'medium' ? 3 : 5;
  if (total <= maxVisible) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  let start = current - Math.floor(maxVisible / 2);
  let end = current + Math.floor(maxVisible / 2);
  if (start < 1) {
    start = 1;
    end = maxVisible;
  } else if (end > total) {
    end = total;
    start = total - maxVisible + 1;
  }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

  constructor() {
    effect(() => {
      const maxPages = this.totalPages();
      const current = this._currentPage();
      if (current > maxPages && maxPages > 0) {
        this._currentPage.set(maxPages);
        this.pageChange.emit(maxPages);
      }
    });
  }

  ngOnInit() {
    this.checkScreenSize();
    this.resizeListener = () => this.checkScreenSize();
    window.addEventListener('resize', this.resizeListener);
  }

  ngOnDestroy() {
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  private checkScreenSize() {
  const width = window.innerWidth;
  if (width < 640) this._screenSize.set('small');
  else if (width < 740) this._screenSize.set('medium');
  else this._screenSize.set('large');
}

  goToPage(page: number) {
    const maxPages = this.totalPages();
    if (page < 1 || page > maxPages) return;

    this._currentPage.set(page);
    this.pageChange.emit(page);
  }

  onPageSizeChange(newSize: number) {
    this._pageSize.set(newSize);
    this.pageSizeChange.emit(newSize);
  }

  getResultsText(): string {
  const total = this._totalItems();
  if (total === 0) return '0';

  const current = this._currentPage();
  const size = this._pageSize();
  const start = (current - 1) * size + 1;
  const end = Math.min(current * size, total);

  switch (this._screenSize()) {
    case 'small':
      return `${start}-${end} de ${total}`;
    case 'medium':
      return `${start}-${end} de ${total} ${this.itemType}`;
    default:
      return this.itemFormatPaginationText
        .replace('{start}', start.toString())
        .replace('{end}', end.toString())
        .replace('{total}', total.toString());
  }
}
}
